/**
 * Phase 6.0 — Customer Dashboard PREVIEW (non-live mockup).
 *
 * This page renders sample data so the operator can compare the proposed
 * "Featured + Grid" hybrid layout (2-3 hero cards on top, normal grid below)
 * plus the new Cannabinoids + Terpenes detail panels — without touching the
 * live customer dashboard.
 *
 * Route: /preview/dashboard
 *
 * Once approved, the live `CustomerDashboard.js` gets the same treatment
 * wired to real `products` data (with lab_data fields populated by admin).
 */
import { useEffect, useRef, useState } from 'react';
import { ShoppingCart, ChevronDown, Info, Leaf } from 'lucide-react';

// Standard flower size variants used as a default. Real catalog products
// already carry `variants` from the backend — this mirrors that shape.
const DEFAULT_FLOWER_VARIANTS = [
  { id: 'eighth', label: '1/8 oz',  weight_g: 3.5,  price: 35 },
  { id: 'quarter', label: '1/4 oz', weight_g: 7,    price: 65 },
  { id: 'half',    label: '1/2 oz', weight_g: 14,   price: 120 },
  { id: 'ounce',   label: '1 oz',   weight_g: 28,   price: 220 },
];

// Sample products to demonstrate the layout. Real data shape lives on the
// products collection — these fields will all be optional / backward-compat.
const SAMPLE_PRODUCTS = [
  {
    id: 'featured-1',
    name: 'Candy Chrome',
    description: 'Top-shelf indoor flower. Sweet candy nose with chrome-bright lemon citrus and a heavy gas finish. Slow-cured 21 days, hand-trimmed, jarred fresh.',
    vibe_description: 'Sweet candy nose, chrome-bright citrus, heavy gas finish.',
    price: 35,
    image_url: 'https://images.unsplash.com/photo-1603909223429-69bb7101f420?w=600&q=80',
    strain_type: 'Hybrid',
    category: 'Flower',
    featured: true,
    variants: DEFAULT_FLOWER_VARIANTS,
    lab_data: {
      cannabinoids: { THCA: 31.3, 'THC-D9': 0.91, CBGA: 0.82, THCVA: 0.20, CBG: 0.09, CBDA: 0.07, CBD: 0.06, CBC: 0.05 },
      terpenes: { total: 2.51, 'Beta Caryophyllene': 0.71, 'Beta Myrcene': 0.52, Limonene: 0.47, Humulene: 0.22, Linalool: 0.19, 'Beta Pinene': 0.10, Bisabolol: 0.10 },
    },
  },
  {
    id: 'featured-2',
    name: 'Apple Fritter',
    description: 'A creamy, pastry-forward hybrid. Notes of warm apple, vanilla, and cheese-like funk. Smooth on the exhale.',
    vibe_description: 'Warm apple, vanilla pastry, smooth funk.',
    price: 35,
    image_url: 'https://images.unsplash.com/photo-1536819114556-1c10f2b9c2dd?w=600&q=80',
    strain_type: 'Hybrid',
    category: 'Flower',
    featured: true,
    variants: DEFAULT_FLOWER_VARIANTS,
    lab_data: {
      cannabinoids: { THCA: 27.8, 'THC-D9': 0.78, CBGA: 0.65, CBG: 0.11, CBD: 0.04 },
      terpenes: { total: 2.10, 'Beta Caryophyllene': 0.55, Limonene: 0.40, 'Beta Myrcene': 0.38, Linalool: 0.22 },
    },
  },
  // Regular grid items — full lab_data so the detail drawer looks identical
  ...['Watermelon Z', 'Gelato 41', 'Zkittlez', 'Wedding Cake', 'Runtz', 'Northern Lights'].map((n, i) => ({
    id: `grid-${i}`,
    name: n,
    description: [
      'Sunday-afternoon sativa. Juicy watermelon rind, a touch of black pepper, lifted clear-headed energy.',
      'Indica-leaning hybrid. Creamy dessert profile — vanilla bean, sweet cream, a soft skunk underline.',
      'Crowd-favorite hybrid. Tropical Skittles candy nose, mellow body, social-friendly.',
      'Heavy indica. Earthy vanilla cake, almond, deeply relaxing — pair with a quiet night.',
      'Hybrid. Sweet gas candy, balanced euphoria, party-ready without being heavy.',
      'Classic landrace indica. Pine, earth, hash spice — the wind-down strain.',
    ][i],
    vibe_description: ['Bright, juicy, lifted', 'Creamy dessert, balanced', 'Fruity, mellow, social', 'Earthy vanilla, calm', 'Sweet candy energy', 'Classic indica wind-down'][i],
    price: 35,
    image_url: `https://images.unsplash.com/photo-${['1603909223429-69bb7101f420', '1536819114556-1c10f2b9c2dd', '1599546499011-cccac2b88de7', '1542456885-89667376a074', '1611078489935-0cb964de46d6', '1574874090569-4ce05bd47de3'][i]}?w=400&q=80`,
    strain_type: ['Sativa', 'Indica', 'Hybrid', 'Indica', 'Hybrid', 'Indica'][i],
    category: 'Flower',
    variants: DEFAULT_FLOWER_VARIANTS,
    lab_data: {
      cannabinoids: {
        THCA: [24.6, 28.1, 26.2, 29.4, 25.8, 22.9][i],
        'THC-D9': [0.71, 0.85, 0.66, 0.92, 0.74, 0.61][i],
        CBGA: [0.55, 0.68, 0.71, 0.49, 0.62, 0.58][i],
        CBG: [0.08, 0.12, 0.10, 0.07, 0.11, 0.09][i],
        CBD: [0.04, 0.06, 0.05, 0.05, 0.04, 0.07][i],
      },
      terpenes: {
        total: [2.20, 2.45, 2.05, 2.65, 2.30, 1.95][i],
        'Beta Caryophyllene': [0.48, 0.62, 0.45, 0.71, 0.58, 0.42][i],
        'Beta Myrcene': [0.35, 0.40, 0.30, 0.55, 0.42, 0.50][i],
        Limonene: [0.55, 0.32, 0.50, 0.28, 0.46, 0.18][i],
        Humulene: [0.18, 0.22, 0.16, 0.25, 0.20, 0.17][i],
        Linalool: [0.12, 0.18, 0.10, 0.24, 0.16, 0.21][i],
      },
    },
  })),
];

export default function CustomerDashboardPreview() {
  const [openDetailId, setOpenDetailId] = useState(null);

  const featured = SAMPLE_PRODUCTS.filter((p) => p.featured);
  const grid = SAMPLE_PRODUCTS.filter((p) => !p.featured);
  const openProduct = SAMPLE_PRODUCTS.find((p) => p.id === openDetailId) || null;

  return (
    <div className="min-h-screen bg-[#050003] text-[#A3C7A8] p-4 sm:p-6" data-testid="customer-dashboard-preview">
      {/* PREVIEW header strip */}
      <div className="max-w-5xl mx-auto mb-6 bg-[#FF9F0A]/10 border border-[#FF9F0A] p-3 rounded-none font-mono text-xs uppercase">
        <span className="text-[#FF9F0A] font-bold">// PREVIEW MODE</span>{' '}
        <span className="text-[#A3C7A8]">Not live · all data is sample · scroll down to see Cannabinoids + Terpenes detail panel · tap any card to open.</span>
      </div>

      <div className="max-w-5xl mx-auto space-y-8">
        {/* FEATURED hero cards */}
        <section>
          <h2 className="text-[#39FF14] font-mono uppercase tracking-wider text-xs mb-3">
            {'>'} TODAY&apos;S DROPS_
          </h2>
          <div className="space-y-3">
            {featured.map((p) => (
              <FeaturedCard key={p.id} product={p} onOpen={() => setOpenDetailId(p.id)} />
            ))}
          </div>
        </section>

        {/* GRID — rest of the catalog (existing-style cards) */}
        <section>
          <h2 className="text-[#39FF14] font-mono uppercase tracking-wider text-xs mb-3">
            {'>'} FULL MENU_
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {grid.map((p) => (
              <GridCard key={p.id} product={p} onOpen={() => setOpenDetailId(p.id)} />
            ))}
          </div>
        </section>
      </div>

      {/* DETAIL drawer — overlay style */}
      {openProduct && (
        <DetailDrawer product={openProduct} onClose={() => setOpenDetailId(null)} />
      )}
    </div>
  );
}

function FeaturedCard({ product, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left bg-gradient-to-br from-[#0C1810] to-[#0C0612] border border-[#2A0F3D] hover:border-[#39FF14] transition-colors rounded-none p-3 sm:p-4 flex gap-3 sm:gap-4 group"
      data-testid={`preview-featured-${product.id}`}
    >
      <div className="w-28 sm:w-40 flex-shrink-0">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full aspect-square object-cover rounded-sm"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-white font-bold text-lg sm:text-xl uppercase tracking-wide leading-tight">
            {product.name}
          </h3>
          <span className="text-[#39FF14] font-mono font-bold whitespace-nowrap">
            ${product.price}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] font-mono uppercase tracking-wider border border-[#2A0F3D] text-[#A3C7A8] px-1.5 py-0.5 rounded-none">
            {product.strain_type}
          </span>
          {product.lab_data?.cannabinoids?.THCA && (
            <span className="text-[10px] font-mono text-[#39FF14]">
              THCA {product.lab_data.cannabinoids.THCA}%
            </span>
          )}
        </div>
        <p className="text-[#A3C7A8] text-sm mt-2 line-clamp-3 leading-relaxed">
          {product.description}
        </p>
        <div className="text-[#39FF14] text-xs font-mono mt-2 opacity-70 group-hover:opacity-100 flex items-center gap-1">
          View details <ChevronDown className="w-3 h-3" />
        </div>
      </div>
    </button>
  );
}

function GridCard({ product, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="bg-[#0C0612] border border-[#2A0F3D] hover:border-[#39FF14] transition-colors rounded-none overflow-hidden text-left"
      data-testid={`preview-grid-${product.id}`}
    >
      <img src={product.image_url} alt={product.name} className="w-full aspect-square object-cover" />
      <div className="p-2">
        <p className="text-[#39FF14] font-mono text-xs font-bold truncate">{product.name}</p>
        <p className="text-[#6B8E73] text-[10px] font-mono truncate">{product.vibe_description}</p>
        <p className="text-[#39FF14] text-sm font-bold mt-1">${product.price}</p>
      </div>
    </button>
  );
}

function DetailDrawer({ product, onClose }) {
  const cb = product.lab_data?.cannabinoids;
  const tp = product.lab_data?.terpenes;
  const variants = product.variants || [];
  const [selectedVariantId, setSelectedVariantId] = useState(variants[0]?.id || null);
  const selectedVariant = variants.find((v) => v.id === selectedVariantId) || null;
  const displayPrice = selectedVariant?.price ?? product.price;
  // Track when the SIZE SELECTOR (not the inline CTA) has fully scrolled out
  // of view. While the size grid is visible, the user can already see prices
  // and tap to act — the sticky bar would just overlap content. It only
  // appears once they've scrolled past the entire selector block into the
  // lab data panels below.
  const variantsBlockRef = useRef(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  useEffect(() => {
    const el = variantsBlockRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShowStickyCta(true);  // fallback for old browsers / missing variants
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      className="fixed inset-0 z-50 bg-[#050003]/95 backdrop-blur-sm overflow-y-auto"
      data-testid="preview-detail-drawer"
    >
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5 pb-32">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <span className="text-[#39FF14] font-mono text-xs uppercase tracking-wider">
            {'>'} PRODUCT_DETAIL
          </span>
          <button
            onClick={onClose}
            className="border border-[#FF3B30] text-[#FF3B30] px-3 py-1 font-mono text-xs uppercase rounded-none"
            data-testid="preview-detail-close"
          >
            ✕ CLOSE
          </button>
        </div>

        {/* Photo + header */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-4">
          <img src={product.image_url} alt={product.name} className="w-full aspect-video object-cover rounded-sm mb-3" />
          <h1 className="text-white text-2xl font-bold uppercase">{product.name}</h1>
          <div className="flex items-center gap-2 mt-1 mb-3">
            <span className="text-[10px] font-mono uppercase border border-[#39FF14] text-[#39FF14] px-1.5 py-0.5">
              {product.strain_type}
            </span>
            <span className="text-[#39FF14] font-mono font-bold">from ${variants[0]?.price ?? product.price}</span>
          </div>
          <p className="text-[#A3C7A8] text-sm leading-relaxed">{product.description}</p>
        </div>

        {/* Variant size selector (flower tiers — 1/8, 1/4, 1/2, 1oz) */}
        {variants.length > 0 && (
          <div ref={variantsBlockRef} className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="preview-variants">
            <h3 className="text-[#39FF14] font-mono uppercase tracking-wider text-sm mb-3">
              {'>'} SELECT SIZE
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {variants.map((v) => {
                const active = v.id === selectedVariantId;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setSelectedVariantId(v.id)}
                    data-testid={`preview-variant-${v.id}`}
                    className={`p-3 border rounded-none text-center transition-colors ${
                      active
                        ? 'border-[#39FF14] bg-[rgba(57,255,20,0.08)]'
                        : 'border-[#2A0F3D] hover:border-[#A3C7A8]'
                    }`}
                  >
                    <div className={`font-mono font-bold text-base ${active ? 'text-[#39FF14]' : 'text-white'}`}>
                      {v.weight_g}g
                    </div>
                    <div className={`font-mono font-bold text-lg mt-1 ${active ? 'text-[#39FF14]' : 'text-[#A3C7A8]'}`}>
                      ${v.price}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Primary CTA — placed directly under the size selector so the
                customer can act the instant they pick. The bottom sticky CTA
                is a secondary fallback for when this one scrolls off screen. */}
            <button
              type="button"
              className="w-full mt-3 bg-[#39FF14] text-[#050003] font-mono font-bold uppercase tracking-wider px-4 py-3 rounded-none flex items-center justify-center gap-2"
              data-testid="preview-add-to-cart-inline"
            >
              <ShoppingCart className="w-4 h-4" />
              ADD TO CART
            </button>
          </div>
        )}

        {/* Cannabinoids panel */}
        {cb && Object.keys(cb).length > 0 && (
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="preview-cannabinoids">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#39FF14] font-mono uppercase tracking-wider text-sm">
                {'>'} CANNABINOIDS
              </h3>
              <span className="text-[10px] text-[#6B8E73] font-mono">// from COA</span>
            </div>
            <p className="text-[#A3C7A8] text-xs leading-relaxed mb-3">
              Chemical compounds in cannabis that interact with the body&apos;s endocannabinoid system.
            </p>

            {/* HERO bar — the headline THCA */}
            {cb.THCA && (
              <div className="mb-3">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-white font-mono font-bold text-sm">THCA</span>
                  <span className="text-[#39FF14] font-mono font-bold">{cb.THCA}%</span>
                </div>
                <div className="h-2 bg-black border border-[#2A0F3D]">
                  <div
                    className="h-full bg-[#39FF14]"
                    style={{ width: `${Math.min(cb.THCA * 2, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Chip grid for the rest */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-xs font-mono">
              {Object.entries(cb)
                .filter(([k]) => k !== 'THCA')
                .map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-center justify-between bg-black/30 border border-[#2A0F3D] px-2 py-1.5"
                    data-testid={`preview-cb-${k}`}
                  >
                    <span className="text-[#A3C7A8] uppercase">{k}</span>
                    <span className="text-[#39FF14]">{v}%</span>
                    <Info className="w-3 h-3 text-[#6B8E73] ml-1" />
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Terpenes panel */}
        {tp && Object.keys(tp).length > 0 && (
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="preview-terpenes">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#39FF14] font-mono uppercase tracking-wider text-sm">
                {'>'} TERPENES
              </h3>
              <span className="text-[10px] text-[#6B8E73] font-mono">// flavor & effect</span>
            </div>
            <p className="text-[#A3C7A8] text-xs leading-relaxed mb-3">
              Terpenes are key to the smell and taste of cannabis. They team up with cannabinoids to produce the variety of effects cannabis is known for.
            </p>

            {tp.total && (
              <div className="mb-3 bg-black/30 border border-[#39FF14]/40 p-2 flex items-center justify-between">
                <span className="text-white font-mono font-bold text-sm">Total Terpenes</span>
                <span className="text-[#39FF14] font-mono font-bold text-lg">{tp.total}%</span>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(tp)
                .filter(([k]) => k !== 'total')
                .map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-center gap-2 bg-black/30 border border-[#2A0F3D] px-2 py-2"
                    data-testid={`preview-tp-${k}`}
                  >
                    <Leaf className="w-4 h-4 text-[#39FF14] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[#A3C7A8] text-[10px] font-mono leading-tight truncate">{k}</p>
                      <p className="text-[#39FF14] text-xs font-mono font-bold">{v}%</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky CTA — only appears when inline CTA has scrolled out of view.
          Prevents the green-bar-through-the-size-grid look. */}
      {showStickyCta && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-[#050003] border-t border-[#2A0F3D] p-3 shadow-[0_-8px_24px_rgba(0,0,0,0.6)]"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
          data-testid="preview-sticky-cta-wrapper"
        >
        <div className="max-w-2xl mx-auto">
          <button
            type="button"
            className="w-full bg-[#39FF14] text-[#050003] font-mono font-bold uppercase tracking-wider px-4 py-3 rounded-none flex items-center justify-center gap-2"
            data-testid="preview-add-to-cart"
          >
            <ShoppingCart className="w-4 h-4" />
            ADD TO CART
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
